/**
 * OQ-291 · Chi tiết sub-domain — bản ghi DNS trong nhánh + chia sẻ.
 *
 * ── Vì sao trang này KHÔNG dùng lại `DomainDetailPage` ─────────────────────
 *
 * Trang chi tiết tên miền có tab SSL, gói, nameserver, xoá cache — tất cả đều
 * thuộc **cả vùng DNS**, và người giữ một sub-domain không có quyền nào trong
 * số đó. Dựng lại nó rồi ẩn bốn tab sẽ để lại một màn hình nói dối về phạm vi:
 * người dùng thấy khung của một thứ lớn hơn thứ họ thật sự cầm.
 *
 * ── Nút vẽ theo quyền MÁY CHỦ trả về, không theo suy đoán ──────────────────
 *
 * `GET /subdomains/:id` trả `capabilities` đã lọc qua `SUB_ALLOWED_CAPABILITIES`.
 * Giao diện chỉ đọc mảng ấy. Nếu tự suy từ mức quyền, nó sẽ vẽ nút "Chia sẻ"
 * cho người mang mức `owner` trên sub — đúng thứ quy tắc một cấp cấm, và người
 * dùng bấm vào sẽ nhận 403 mà không hiểu vì sao.
 */

import { useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, thanhApiError, type ApiError } from '../lib/api';
import { useT } from '../lib/i18n';
import { Icon } from '../components/Icon';
import { Modal } from '../components/Modal';
import { ApiErrorAlert } from '../components/ApiErrorAlert';
import { AsyncSection, EmptyState, SkeletonCard } from '../components/States';
import { useToast } from '../components/Toast';
import { useFormat } from '../lib/format';
import { EmailLookupField, type EmailLookupState } from '../components/EmailLookupField';
import type { SubdomainItem } from './SubdomainsPage';
import { DnsPanel } from './DomainDetailPage';

interface SubdomainDetail extends SubdomainItem {
  capabilities: string[];
}

/** Lời mời chưa dùng trên nhánh con — OQ-309. */
interface SubPending {
  id: string;
  email: string;
  expiresAt: string | null;
  createdAt: string | null;
}

interface SubGrant {
  id: string;
  userId: string;
  userName: string;
  userEmail: string;
  domainRoleId: string;
  roleName: string;
  roleSlug: string;
  effectiveCapabilities: string[];
  createdAt: string;
}

const TABS = ['records', 'sharing'] as const;
type Tab = (typeof TABS)[number];

export function SubdomainDetailPage(): JSX.Element {
  const { id = '' } = useParams();
  const t = useT();

  /*
   * Tab nằm trong URL chứ không chỉ trong state.
   *
   * Cột "Chia sẻ" ở trang danh sách dẫn thẳng tới đây bằng `?tab=sharing`. Giữ
   * tab trong `useState` thì cái link ấy luôn mở nhầm sang bảng bản ghi — người
   * bấm vào con số 3 lại phải tự đi tìm ba người đó ở đâu.
   *
   * Kèm theo là hai thứ được không: F5 giữ nguyên chỗ đang xem, và gửi link cho
   * người khác thì họ mở ra đúng cái đang nói tới.
   */
  const [params, setParams] = useSearchParams();
  const tuUrl = params.get('tab');
  const tab: Tab = TABS.includes(tuUrl as Tab) ? (tuUrl as Tab) : 'records';

  const doiTab = (x: Tab): void => {
    setParams(
      (truoc) => {
        const sau = new URLSearchParams(truoc);
        sau.set('tab', x);
        return sau;
      },
      { replace: true },
    );
  };

  const chiTiet = useQuery({
    queryKey: ['subdomain', id],
    queryFn: () => api.get<SubdomainDetail>(`/subdomains/${id}`),
  });

  const sub = chiTiet.data;
  const co = (cap: string): boolean => Boolean(sub?.capabilities.includes(cap));

  return (
    <div data-od-id="page-subdomain-detail">
      <AsyncSection
        loading={chiTiet.isLoading}
        error={chiTiet.error}
        data={sub}
        onRetry={() => void chiTiet.refetch()}
        skeleton={<SkeletonCard rows={6} />}
      >
        {(s) => (
          <>
            <div className="page-head">
              <div className="grow">
                <span className="eyebrow">
                  <Link to="/subdomains">{t('nav.subdomains')}</Link>
                </span>
                <h1 data-od-id="subdomain-detail-title">{s.fqdn}</h1>
                <p className="lede">
                  {t('subdomains.detail.lede', { domain: s.domainName })}
                  {s.relation === 'granted' ? (
                    <>
                      {' '}
                      <span className="chip accent">{t('subdomains.relation.granted')}</span>
                    </>
                  ) : null}
                </p>
              </div>
            </div>

            <div className="tabs" role="tablist" data-od-id="subdomain-tabs">
              {TABS.filter((x) => x !== 'sharing' || co('sub.share')).map((x) => (
                <button
                  key={x}
                  type="button"
                  role="tab"
                  aria-selected={tab === x}
                  className={tab === x ? 'active' : undefined}
                  onClick={() => doiTab(x)}
                  data-od-id={`subdomain-tab-${x}`}
                >
                  {t(`subdomains.tab.${x}`)}
                </button>
              ))}
            </div>

            {tab === 'records' ? (
              /*
                OQ-320 — dùng CHUNG bảng bản ghi với trang tên miền chính.

                **Chủ dự án**: *"thay đổi giao diện trang quản lý bản ghi của tên
                miền con giống như trang quản lý bản ghi của tên miền chính […]
                Hiện tại đang quá đơn giản, không bật tắt được proxy và chỉnh sửa
                record"*.

                Máy chủ đã có đủ đường đi từ OQ-291 — `PATCH …/records/:rid`,
                `…/records/:rid/proxy` — chỉ giao diện là chưa dùng tới. Nên việc
                cần làm là cho hai trang dùng chung MỘT bảng, không phải dựng
                bảng thứ hai.

                Bảng ấy đi kèm cả những thứ không nhìn thấy từ ảnh chụp: hoàn tác
                xoá trong 10 giây (FR-040), sắp xếp theo cột, lọc theo loại, phân
                trang nhớ trên URL. Chép tay sang đây thì thiếu đúng những thứ đó,
                và không ai phát hiện cho tới lúc cần dùng.
              */
              <DnsPanel
                nguon={{
                  base: `/subdomains/${s.id}`,
                  id: s.id,
                  ten: s.fqdn,
                  khoaChiTiet: ['subdomain', s.id],
                }}
                allows={co}
              />
            ) : (
              <BangChiaSe sub={s} />
            )}
          </>
        )}
      </AsyncSection>
    </div>
  );
}

function BangChiaSe({ sub }: { sub: SubdomainDetail }): JSX.Element {
  const t = useT();
  const { formatDateTime } = useFormat();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [moCap, setMoCap] = useState(false);

  const ds = useQuery({
    queryKey: ['subdomain-grants', sub.id],
    queryFn: () =>
      api.get<{ items: SubGrant[]; pending: SubPending[] }>(`/subdomains/${sub.id}/grants`),
  });

  const thuHoi = useMutation({
    mutationFn: (gid: string) => api.del(`/subdomains/${sub.id}/grants/${gid}`),
    onSuccess: async () => {
      toast(t('subdomains.share.revoked'));
      await queryClient.invalidateQueries({ queryKey: ['subdomain-grants', sub.id] });
    },
  });

  /** Thu hồi lời mời chưa dùng — OQ-309. */
  const thuHoiMoi = useMutation({
    mutationFn: (pid: string) => api.del(`/subdomains/${sub.id}/pending/${pid}`),
    onSuccess: async () => {
      toast(t('subdomains.share.inviteRevoked'));
      await queryClient.invalidateQueries({ queryKey: ['subdomain-grants', sub.id] });
    },
  });

  /*
   * Gộp hai danh sách để quyết định "trống hay không" — OQ-309.
   *
   * `AsyncSection` nhận `data` và tự hiện `EmptyState` khi rỗng. Nếu chỉ đưa
   * `items` thì một sub-domain đã mời hai người nhưng chưa ai kích hoạt vẫn hiện
   * **"Chưa chia sẻ cho ai"** — đúng câu khiến chủ dự án tin là chia sẻ hỏng.
   */
  const coGiDo = (ds.data?.items.length ?? 0) + (ds.data?.pending.length ?? 0) > 0;

  return (
    <div className="card">
      <div className="card-head">
        <h2>{t('subdomains.tab.sharing')}</h2>
        <button
          className="btn btn-primary btn-sm"
          type="button"
          onClick={() => setMoCap(true)}
          data-od-id="subdomain-share-add"
        >
          <Icon name="plus" size={14} />
          {t('subdomains.share.add')}
        </button>
      </div>

      {/*
        Nói thẳng giới hạn ngay trên bảng.

        Người cấp quyền cần biết TRƯỚC rằng người nhận sẽ không chia tiếp được
        — nếu không, họ cấp mức `owner` rồi tưởng đã uỷ quyền trọn vẹn, và chỉ
        phát hiện khi người kia báo là không làm được.
      */}
      <div className="scope-note" role="note" data-od-id="subdomain-share-note">
        <Icon name="info" size={16} />
        <span>{t('subdomains.share.oneLevelNote')}</span>
      </div>

      <AsyncSection
        loading={ds.isLoading}
        error={ds.error}
        data={coGiDo ? ds.data : undefined}
        onRetry={() => void ds.refetch()}
        skeleton={<SkeletonCard rows={3} />}
        empty={
          <EmptyState
            icon="users"
            title={t('subdomains.share.emptyTitle')}
            body={t('subdomains.share.emptyBody')}
          />
        }
      >
        {(data) => (
          <div className="table-wrap">
            <table className="tbl" data-od-id="subdomain-grants-table">
              <thead>
                <tr>
                  <th scope="col">{t('subdomains.share.col.user')}</th>
                  <th scope="col">{t('subdomains.share.col.role')}</th>
                  <th scope="col">{t('subdomains.share.col.effective')}</th>
                  <th scope="col">{t('subdomains.col.created')}</th>
                  <th scope="col" className="right" />
                </tr>
              </thead>
              <tbody>
                {data.items.map((g) => (
                  <tr key={g.id}>
                    <td>
                      <b>{g.userName}</b>
                      <div className="sub small muted">{g.userEmail}</div>
                    </td>
                    <td>
                      <span className="chip">{g.roleName}</span>
                    </td>
                    <td className="small muted">
                      {t('subdomains.share.effectiveCount', {
                        n: g.effectiveCapabilities.length,
                      })}
                    </td>
                    <td className="small muted">{formatDateTime(g.createdAt)}</td>
                    <td className="right">
                      <button
                        className="icon-btn"
                        type="button"
                        aria-label={t('subdomains.share.revoke')}
                        onClick={() => thuHoi.mutate(g.id)}
                        data-od-id={`subdomain-grant-revoke-${g.id}`}
                      >
                        <Icon name="trash" size={15} />
                      </button>
                    </td>
                  </tr>
                ))}

                {/*
                  Lời mời đang chờ — OQ-309.

                  Cùng bảng, không phải một khối riêng bên dưới: người đọc muốn
                  biết "ai đang có quyền trên nhánh này", và một địa chỉ đã được
                  mời là một câu trả lời hợp lệ cho câu hỏi ấy — chỉ khác ở chỗ
                  nó chưa có hiệu lực.
                */}
                {data.pending.map((p) => (
                  <tr key={p.id} data-od-id={`subdomain-pending-${p.id}`}>
                    <td>
                      <b>{p.email}</b>
                      <div className="sub small muted">{t('subdomains.share.pendingSub')}</div>
                    </td>
                    <td>
                      <span className="chip warn">{t('subdomains.share.pending')}</span>
                    </td>
                    <td className="small muted">
                      {p.expiresAt
                        ? t('subdomains.share.pendingExpires', {
                            date: formatDateTime(p.expiresAt),
                          })
                        : '—'}
                    </td>
                    <td className="small muted">
                      {p.createdAt ? formatDateTime(p.createdAt) : '—'}
                    </td>
                    <td className="right">
                      <button
                        className="icon-btn"
                        type="button"
                        aria-label={t('subdomains.share.revokeInvite')}
                        title={t('subdomains.share.revokeInvite')}
                        onClick={() => thuHoiMoi.mutate(p.id)}
                        data-od-id={`subdomain-pending-revoke-${p.id}`}
                      >
                        <Icon name="trash" size={15} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </AsyncSection>

      {moCap ? (
        <CapQuyenModal
          sub={sub}
          onClose={() => setMoCap(false)}
          onDone={async (mode) => {
            setMoCap(false);
            /* Hai kết cục khác hẳn nhau: một bên quyền có hiệu lực ngay, một bên
               còn chờ người kia mở thư. */
            toast(t(mode === 'invited' ? 'subdomains.share.invited' : 'subdomains.share.granted'));
            await queryClient.invalidateQueries({ queryKey: ['subdomain-grants', sub.id] });
          }}
        />
      ) : null}
    </div>
  );
}

/**
 * Chia sẻ sub-domain bằng EMAIL — OQ-306.
 *
 * ── Vì sao đổi khỏi dropdown ────────────────────────────────────────────────
 *
 * **Chủ dự án**: *"chia sẻ subdomain bị phân quyền sai, người dùng là chủ sở hữu
 * nhưng vẫn không chia sẻ được, chỉ có admin chia sẻ được"*.
 *
 * Bản cũ nạp danh sách người dùng bằng `GET /users`, mà route ấy khai
 * `requireCapability('adm.users')` ở mức module. Người thường nhận **403**, nên
 * dropdown rỗng và không chọn được ai — trông hệt như "không có quyền chia sẻ",
 * trong khi tầng phân quyền sub-domain vẫn đúng từ đầu.
 *
 * `GET /users/lookup` chỉ đòi `share.grant` và chỉ trả về ĐÚNG một người theo
 * email chính xác. Không dò được danh sách, và ai chia sẻ được thì gọi được.
 *
 * Mức quyền không cho chọn nữa: sau khi quy tắc một cấp lọc (OQ-291), mọi mức
 * đều rơi về nhóm `dns.*` — một ô chọn chỉ tạo ảo giác rằng có khác biệt.
 */
function CapQuyenModal({
  sub,
  onClose,
  onDone,
}: {
  sub: SubdomainDetail;
  onClose: () => void;
  onDone: (mode: 'granted' | 'invited') => void | Promise<void>;
}): JSX.Element {
  const t = useT();
  const [tra, setTra] = useState<EmailLookupState | null>(null);
  const [loi, setLoi] = useState<ApiError | null>(null);

  const cap = useMutation({
    mutationFn: () =>
      api.post<{ mode: 'granted' | 'invited' }>(`/subdomains/${sub.id}/invitations`, {
        email: tra?.email ?? '',
      }),
    onSuccess: (kq) => void onDone(kq.mode),
    onError: (e: unknown) => setLoi(thanhApiError(e)),
  });

  /*
   * Nút chính chỉ bật SAU khi tra cứu — giống hệt modal tên miền.
   *
   * Không phải để cản: đó là cách hộp thoại này trả lời câu *"tôi đang cấp
   * quyền cho ai?"* mà không cần một hộp thoại thứ hai. Người dùng gõ email,
   * bấm tra, thấy tên người ấy hiện ra ngay dưới ô — rồi mới bấm Cấp quyền.
   */
  const sanSang = Boolean(tra && (tra.found || tra.missing) && !tra.looking);

  return (
    <Modal open title={t('subdomains.share.addTitle')} onClose={onClose}>
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          setLoi(null);
          cap.mutate();
        }}
      >
        <ApiErrorAlert error={loi} />

        <EmailLookupField
          idPrefix="sub-share"
          label={t('subdomains.share.email')}
          hintIdle={t('subdomains.share.emailHint')}
          onChange={setTra}
        />

        <div className="row" style={{ gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
          <button className="btn" type="button" onClick={onClose}>
            {t('common.cancel')}
          </button>
          <button
            className="btn btn-primary"
            type="submit"
            disabled={cap.isPending || !sanSang}
            data-od-id="sub-share-submit"
          >
            {cap.isPending
              ? t('common.processing')
              : tra?.missing
                ? t('perm.sendInvite')
                : t('subdomains.share.add')}
          </button>
        </div>
      </form>
    </Modal>
  );
}
